import { ComponentQueryLoader } from "../../../../../shared/component-query-loader";
import { AdditionalFiles } from "./additional-files";
import { useMutation, useQuery } from "@apollo/client";
import {
  CasesCredentialVerificationAdditionalFilesContainerCreateAuthHeaderMutationDocument,
  CasesCredentialVerificationAdditionalFilesContainerFileRemoveDocument,
  CasesCredentialVerificationAdditionalFilesContainerQueryDocument,
  FileInfo,
} from "../../../../../../generated";
import { AuthResult } from "../../../../../shared/file-upload/azure-upload";
import { FC, useState } from "react";

const ADDITIONAL_FILES_DIRECTORY_SLUG = "additional-private-files/";

interface AdditionalFilesContainerProps {
  caseState: string;
  caseId: string;
  isCurrentAssigned: boolean;
  disabled?: boolean;
}

export const AdditionalFilesContainer: FC<AdditionalFilesContainerProps> = (props) => {
  const {
    data: filesData,
    loading: filesLoading,
    refetch: refetchFiles,
  } = useQuery(
    CasesCredentialVerificationAdditionalFilesContainerQueryDocument,
    {
      variables: {
        credentialVerificationCaseV1Id: props.caseId,
      },
    }
  );

  const [getAuthHeader] = useMutation(
    CasesCredentialVerificationAdditionalFilesContainerCreateAuthHeaderMutationDocument
  );

  const [fileRemove] = useMutation(
    CasesCredentialVerificationAdditionalFilesContainerFileRemoveDocument
  );

  const [isUploadProcessing, setIsUploadProcessing] = useState(false);

  const handleAuthorizeUploadRequest = async (
    file: File
  ): Promise<AuthResult> => {
    try {
      setIsUploadProcessing(true);
      const response = await getAuthHeader({
        variables: {
          input: {
            caseId: props.caseId,
            fileName: ADDITIONAL_FILES_DIRECTORY_SLUG + file.name,
            contentType: file.type,
            contentLength: file.size,
          },
        },
      });
      return {
        ...response?.data?.credentialVerificationCaseV1FileCreateAuthHeader,
        ...{ isAuthorized: true },
      } as AuthResult;
    } catch (e) {
      console.error("Error uploading asset: ", e);
      setIsUploadProcessing(false);
      return { isAuthorized: false } as AuthResult;
    }
  };

  const handleSuccessfulUpload = async () => {
    await refetchFiles();
    setIsUploadProcessing(false);
  };


  const handleDelete = async (fileName: string) => {
    try {
      await fileRemove({
        variables: {
          input: {
            caseId: props.caseId,
            fileName: ADDITIONAL_FILES_DIRECTORY_SLUG + fileName,
          },
        },
      });
      await refetchFiles();
    } catch (e) {
      console.error("Error deleting file: ", e);
    }
  };

  const files: FileInfo[] =
    (filesData?.credentialVerificationCaseV1?.files as FileInfo[]) ?? [];

  return (
    <ComponentQueryLoader
      loading={filesLoading}
      hasData={true}
      hasDataComponent={
        <AdditionalFiles
          onAuthorizeUploadRequest={handleAuthorizeUploadRequest}
          onSuccessfulUpload={handleSuccessfulUpload}
          files={files}
          onDelete={handleDelete}
          caseState={props.caseState}
          isCurrentAssigned={props.isCurrentAssigned}
          isUploadProcessing={isUploadProcessing}
          disabled={props.disabled}
        />
      }
    />
  );
};
